/**
 * GHS 诊断页面 — GhsDetail（v5.1 双仪表盘：指标字典 §13 / PRD 附录 B）。
 *
 * GET /api/products/{id}             产品名（标题/面包屑）
 * GET /api/products/{id}/diagnosis   诊断历史（区块 A：网站质量分六维/评级/环比/问题清单/易修复项）
 * GET /api/products/{id}/geo-score   GEO 综合分六维聚合（区块 B，批次制）
 *
 * 结构：产品子页签 → 区块 A「网站质量分」（总分卡 + 短板建议 | 维度分明细 cmp bars）→
 *       区块 B「GEO 综合分」（六维雷达 + 各维直读 + 样本量 n）→
 *       历史诊断记录表（点「查看」切换上方明细到该期）。
 *
 * 展示门禁（§13.3 硬规则）：区块 B 仅在 geo-score 接口返回真实计算数据时渲染；
 * 接口 data=null / 请求失败 → 整个区块 B 不渲染（无空壳、无占位、无"待产出"伪装）。
 * 区块 B 各维：n<100 显示「样本不足」不出分；unavailable 维显示「数据未接入」+ 原因。
 *
 * 概念稿裁剪：诊断周期预告（"每周一 08:00 自动执行/下期时间"）无接口字段，
 * 跳过不虚构；区块 A 维度按真实六维（GHS_DIMENSIONS）渲染，非概念稿五维。
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts";
import PageHeader from "../components/PageHeader";
import GhsRing from "../components/GhsRing";
import Badge from "../components/Badge";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import {
  getProductDetail,
  getProductDiagnosis,
  getProductGeoScore,
} from "../api/products";
import { useCompanyStore } from "../store/companyStore";
import { GHS_DIMENSIONS, GEO_SCORE_DIMENSIONS } from "../utils/constants";
import { formatDateTime, ghsRating } from "../utils/format";
import type { DiagnosisHistory, GeoScore } from "../types";

/** 评级徽章变体。 */
function ratingVariant(score: number): "success" | "info" | "warning" {
  if (score >= 80) return "success";
  if (score >= 60) return "info";
  return "warning";
}

/** 易修复项统一形态（缺陷 C：后端可能返回已解析对象或字符串化 JSON）。 */
interface EasyFixItem {
  id?: string;
  desc: string;
  effort?: string;
}

/**
 * 解析单条易修复项：
 * - 已解析对象 → 直接取 id / desc / effort；
 * - 字符串化 JSON（如 '{"id":"EF004","desc":"...","effort":"medium"}'）→ 解析后取；
 * - 普通字符串 / 其他 → 按纯文本 desc 兜底，绝不把原始 JSON 字符串上屏。
 */
function parseEasyFix(item: any): EasyFixItem {
  let raw: any = item;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return { desc: raw };
    }
  }
  if (raw && typeof raw === "object") {
    return {
      id: typeof raw.id === "string" ? raw.id : undefined,
      desc: String(raw.desc ?? raw.title ?? ""),
      effort: typeof raw.effort === "string" ? raw.effort : undefined,
    };
  }
  return { desc: String(raw ?? "") };
}

/** 修复工作量映射：low/medium/high → 低/中/高（语义色徽标）。 */
const EFFORT_MAP: Record<
  string,
  { label: string; variant: "success" | "warning" | "error" }
> = {
  low: { label: "低", variant: "success" },
  medium: { label: "中", variant: "warning" },
  high: { label: "高", variant: "error" },
};

/** 维度占比条（对齐概念稿 cmp-row）。 */
function DimRow({
  label,
  score,
  weight,
  isLowest,
}: {
  label: string;
  score: number;
  weight: number;
  isLowest: boolean;
}) {
  const color =
    score >= 70 ? "success.main" : score >= 60 ? "warning.main" : "error.main";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <Box sx={{ minWidth: 120, display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
        {isLowest && <Badge label="最低维" variant="warning" />}
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: "semanticBg.neutral",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${Math.min(Math.max(score, 0), 100)}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 3,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 28, textAlign: "right" }}>
        {score}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "text.disabled", minWidth: 48 }}>
        权重 {weight}%
      </Typography>
    </Box>
  );
}

/** GEO 综合分单维直读行：出分 / 样本不足（n<100）/ 数据未接入 三态。 */
function GeoDimRow({
  label,
  desc,
  dim,
}: {
  label: string;
  desc: string;
  dim: GeoScore["dimensions"][string] | undefined;
}) {
  const unavailable = !dim || dim.unavailable;
  const insufficient = !unavailable && !!dim?.insufficient;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "lineSoft",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
          {unavailable && <Badge label="数据未接入" variant="default" />}
          {insufficient && <Badge label="样本不足" variant="warning" />}
        </Box>
        <Typography sx={{ fontSize: 11, color: "text.disabled", lineHeight: 1.6 }}>
          {unavailable ? dim?.reason || "该维数据不可得" : desc}
        </Typography>
      </Box>
      <Box sx={{ textAlign: "right", minWidth: 72 }}>
        {unavailable || insufficient ? (
          <Typography sx={{ fontSize: 13, color: "text.disabled" }}>—</Typography>
        ) : (
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color:
                (dim?.score ?? 0) >= 70
                  ? "success.main"
                  : (dim?.score ?? 0) >= 60
                    ? "warning.main"
                    : "error.main",
            }}
          >
            {dim?.score}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
          n = {dim?.n ?? 0}
        </Typography>
      </Box>
    </Box>
  );
}

export default function GhsDetail() {
  const { productId } = useParams<{ productId: string }>();
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const effectiveProductId = productId || selectedProductId;
  const theme = useTheme();

  const [productName, setProductName] = useState("");
  const [history, setHistory] = useState<DiagnosisHistory[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /** GEO 综合分（区块 B）。null = 接口无数据/未加载完成/请求失败 → 区块 B 不渲染（§13.3 门禁）。 */
  const [geoScore, setGeoScore] = useState<GeoScore | null>(null);

  /** 产品名（标题用，失败静默）。 */
  useEffect(() => {
    if (!effectiveProductId) return;
    getProductDetail(effectiveProductId)
      .then((d) => setProductName(d.product?.name || ""))
      .catch(() => setProductName(""));
  }, [effectiveProductId]);

  /** GEO 综合分（区块 B 门禁取数：失败/null 均不渲染区块 B）。 */
  useEffect(() => {
    setGeoScore(null);
    if (!effectiveProductId) return;
    getProductGeoScore(effectiveProductId)
      .then((data) => setGeoScore(data))
      .catch(() => setGeoScore(null));
  }, [effectiveProductId]);

  /** 诊断历史。 */
  useEffect(() => {
    if (!effectiveProductId) {
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    getProductDiagnosis(effectiveProductId)
      .then((list) => {
        setHistory(list);
        setSelectedId(list[0]?.id || "");
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "获取诊断历史失败");
        setHistory([]);
      })
      .finally(() => setLoading(false));
  }, [effectiveProductId]);

  /** 当前选中的诊断（默认最新一期）。 */
  const current = useMemo(
    () => history.find((d) => d.id === selectedId) || history[0] || null,
    [history, selectedId],
  );

  /** 易修复项解析集（兼容字符串化 JSON 与已解析数组两种形态）。 */
  const easyFixes = useMemo<EasyFixItem[]>(
    () => (current?.easy_fix_items || []).map(parseEasyFix),
    [current],
  );

  /** 维度明细（真实六维）+ 最低维。 */
  const dimRows = useMemo(() => {
    if (!current?.ghs_dimensions) return [];
    const rows = GHS_DIMENSIONS.map((dim) => ({
      key: dim.key,
      label: dim.label,
      weight: dim.weight,
      score: current.ghs_dimensions?.[dim.key]?.score ?? 0,
    }));
    const min = Math.min(...rows.map((r) => r.score));
    return rows.map((r) => ({ ...r, isLowest: r.score === min && rows.length > 1 }));
  }, [current]);

  /** 环比（previous_ghs 为接口字段）。 */
  const delta = useMemo(() => {
    if (!current || current.previous_ghs === null || current.previous_ghs === undefined) {
      return null;
    }
    return current.ghs_total - current.previous_ghs;
  }, [current]);

  /** GEO 综合分雷达数据：仅出分维度有真实值，未出分维度落 0（直读区为准，见区块内说明）。 */
  const geoRadarData = useMemo(() => {
    if (!geoScore) return [];
    return GEO_SCORE_DIMENSIONS.map((d) => ({
      label: d.label,
      score: geoScore.dimensions[d.key]?.score ?? 0,
    }));
  }, [geoScore]);

  /** GEO 综合分出分维度数（≥1 才渲染雷达，避免全 0 空壳图）。 */
  const geoScoredCount = useMemo(() => {
    if (!geoScore) return 0;
    return GEO_SCORE_DIMENSIONS.filter(
      (d) => geoScore.dimensions[d.key]?.score !== null
        && geoScore.dimensions[d.key]?.score !== undefined,
    ).length;
  }, [geoScore]);

  const columns: DataTableColumn<DiagnosisHistory>[] = [
    {
      key: "time",
      label: "诊断时间",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDateTime(row.diagnosis_time)}
        </Typography>
      ),
    },
    {
      key: "score",
      label: "总分",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.ghs_total}</Typography>
      ),
    },
    {
      key: "delta",
      label: "环比",
      align: "center",
      render: (row) => {
        if (row.previous_ghs === null || row.previous_ghs === undefined) {
          return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>;
        }
        const d = row.ghs_total - row.previous_ghs;
        return (
          <Typography
            sx={{
              fontSize: 12,
              color: d >= 0 ? "success.main" : "error.main",
            }}
          >
            {d >= 0 ? `▲ ${d}` : `▼ ${Math.abs(d)}`}
          </Typography>
        );
      },
    },
    {
      key: "rating",
      label: "评级",
      align: "center",
      render: (row) => (
        <Badge
          label={row.ghs_rating || ghsRating(row.ghs_total)}
          variant={ratingVariant(row.ghs_total)}
        />
      ),
    },
    {
      key: "stage",
      label: "生命周期",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.lifecycle_stage || "—"}
        </Typography>
      ),
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) =>
        row.id === current?.id ? (
          <Badge label="当前查看" variant="info" />
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedId(row.id)}
            sx={{ fontSize: 12 }}
          >
            查看报告
          </Button>
        ),
    },
  ];

  if (!effectiveProductId) {
    return (
      <Box>
        <PageHeader
          title="GHS 诊断"
          breadcrumbs={[
            { label: "资产管理" },
            { label: "产品", path: "/products" },
            { label: "GHS 诊断" },
          ]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先在顶栏选择公司和产品"
              subtitle="GHS 诊断按产品维度生成，在工作台顶部选择公司和产品后，再进行 GEO 诊断"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="GHS 诊断" />
        <Loading fullPage />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={productName ? `GHS 诊断 · ${productName}` : "GHS 诊断"}
        breadcrumbs={[
          { label: "资产管理" },
          { label: "产品", path: "/products" },
          ...(productName
            ? [{ label: productName, path: `/products/${effectiveProductId}` }]
            : []),
          { label: "GHS 诊断" },
        ]}
        subtitle="双指标独立定义、独立计算、独立展示（指标字典 §13）：区块 A 网站质量分 · 区块 B GEO 综合分"
      />

      {/* 产品子页签（对齐概念稿 tabs） */}
      <Tabs
        value={4}
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tab
          label="概览"
          component={RouterLink}
          to={`/products/${effectiveProductId}`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="场景分析"
          component={RouterLink}
          to={`/products/${effectiveProductId}/scenes`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="素材库"
          component={RouterLink}
          to={`/products/${effectiveProductId}/materials`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="事实包"
          component={RouterLink}
          to={`/products/${effectiveProductId}/fact-packs`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="GHS 诊断"
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
      </Tabs>

      {error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── 区块 A · 网站质量分（Website Health Score，存量诊断数据，已通）── */}
          {!current ? (
            <Card>
              <CardContent>
                <EmptyState
                  text="暂无诊断记录"
                  source="由诊断 Agent 产出 · 诊断后自动展示"
                />
              </CardContent>
            </Card>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                  区块 A · 网站质量分
                </Typography>
                <Badge label="官网对 AI 的友好度体检" variant="info" />
              </Box>

              {/* 当前诊断：总分卡 + 短板建议 | 维度分明细 */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                    GHS 总分（{formatDateTime(current.diagnosis_time)}）
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <GhsRing score={current.ghs_total} size={88} thickness={7} />
                    <Box>
                      <Badge
                        label={current.ghs_rating || ghsRating(current.ghs_total)}
                        variant={ratingVariant(current.ghs_total)}
                      />
                      {delta !== null && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            mt: 0.75,
                            color: delta >= 0 ? "success.main" : "error.main",
                          }}
                        >
                          {delta >= 0 ? `▲ ${delta}` : `▼ ${Math.abs(delta)}`} 分 vs 上次诊断
                          （前次 {current.previous_ghs}）
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      lineHeight: 1.8,
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "lineSoft",
                    }}
                  >
                    评分由 GHS 诊断引擎按六维加权产出；≥80 健康 / 60–79 关注 / &lt;60 风险，
                    权重口径见指标字典。
                  </Typography>
                </CardContent>
              </Card>

              {/* 短板建议 */}
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Typography variant="h4" sx={{ flexGrow: 1 }}>
                      短板建议
                    </Typography>
                    <Badge label="优先" variant="warning" />
                  </Box>
                  {dimRows.length > 0 && (
                    <Typography sx={{ fontSize: 13, lineHeight: 1.8, mb: 1.5 }}>
                      <b>{dimRows.find((d) => d.isLowest)?.label}（
                        {dimRows.find((d) => d.isLowest)?.score}）</b>
                      为当前最低维度
                      {current.problem_list && current.problem_list.length > 0
                        ? `，问题清单共 ${current.problem_list.length} 项待处理。`
                        : "。"}
                    </Typography>
                  )}
                  {current.stage_recommendation && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        lineHeight: 1.8,
                        p: 1.5,
                        bgcolor: "brand.50",
                        borderRadius: 1.5,
                      }}
                    >
                      {current.stage_recommendation}
                    </Typography>
                  )}
                  {current.problem_list && current.problem_list.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 500, mb: 0.5 }}>
                        问题清单（{current.problem_list.length}）
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {current.problem_list.map((problem: any, i: number) => (
                          <Chip
                            key={i}
                            label={
                              typeof problem === "string"
                                ? problem
                                : problem.desc || problem.title || JSON.stringify(problem)
                            }
                            size="small"
                            sx={{ fontSize: 11, bgcolor: "semanticBg.error", color: "error.main" }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {easyFixes.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: "success.main", fontWeight: 500, mb: 0.5 }}>
                        易修复项（{easyFixes.length}）
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {easyFixes.map((fix, i) => {
                          const effort = fix.effort
                            ? EFFORT_MAP[fix.effort.toLowerCase()]
                            : undefined;
                          return (
                            <Box
                              key={fix.id || i}
                              sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}
                            >
                              {fix.id && <Badge label={fix.id} variant="info" />}
                              <Typography
                                sx={{ fontSize: 12, color: "text.primary", flexGrow: 1, lineHeight: 1.6 }}
                              >
                                {fix.desc}
                              </Typography>
                              {effort && (
                                <Badge label={`工作量 ${effort.label}`} variant={effort.variant} />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* 维度分明细 */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Typography variant="h4" sx={{ flexGrow: 1 }}>
                      维度分明细
                    </Typography>
                    <Badge label={formatDateTime(current.diagnosis_time)} variant="info" />
                  </Box>
                  {dimRows.length === 0 ? (
                    <EmptyState text="本期无维度明细" />
                  ) : (
                    dimRows.map((d) => (
                      <DimRow
                        key={d.key}
                        label={d.label}
                        score={d.score}
                        weight={d.weight}
                        isLowest={d.isLowest}
                      />
                    ))
                  )}
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      lineHeight: 1.8,
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "lineSoft",
                    }}
                  >
                    六维加权得总分 {current.ghs_total}；最低维以徽标标出，修复建议见
                    左侧「短板建议」。历史各期维度明细可通过下方记录表逐期查看。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
            </>
          )}

          {/* ── 区块 B · GEO 综合分（GEO Health Score，批次制六维）──
              门禁（§13.3）：geoScore 为 null（接口无数据/请求失败/未上线）时
              整个区块不渲染——禁止空壳、占位图、"待产出"伪装。
              与区块 A 相互独立：无诊断记录时区块 B 仍可渲染。 */}
          {geoScore && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                  区块 B · GEO 综合分
                </Typography>
                <Badge label="品牌在 AI 回答中的露出健康度" variant="info" />
                <Badge label={`测量批次 ${geoScore.batch_date}`} />
              </Box>
              <Grid container spacing={2}>
                {/* 六维雷达：仅当 ≥1 维出分时渲染，避免全 0 空壳图 */}
                {geoScoredCount > 0 && (
                  <Grid item xs={12} md={5}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                          六维雷达
                        </Typography>
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart data={geoRadarData} outerRadius="72%">
                            <PolarGrid stroke={theme.palette.divider} />
                            <PolarAngleAxis
                              dataKey="label"
                              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                            />
                            <PolarRadiusAxis
                              domain={[0, 100]}
                              tick={false}
                              axisLine={false}
                            />
                            <Radar
                              dataKey="score"
                              stroke={theme.palette.primary.main}
                              fill={theme.palette.primary.main}
                              fillOpacity={0.28}
                              isAnimationActive={false}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                        <Typography
                          sx={{ fontSize: 11, color: "text.disabled", lineHeight: 1.6 }}
                        >
                          未出分维度（样本不足 / 数据未接入）在雷达上落 0 值，
                          各维真实状态以右侧直读区为准。
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {/* 各维直读 + 样本量 n（六维独立展示，不合成总分） */}
                <Grid item xs={12} md={geoScoredCount > 0 ? 7 : 12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" sx={{ mb: 1 }}>
                        各维直读
                      </Typography>
                      {GEO_SCORE_DIMENSIONS.map((d) => (
                        <GeoDimRow
                          key={d.key}
                          label={d.label}
                          desc={d.desc}
                          dim={geoScore.dimensions[d.key]}
                        />
                      ))}
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.disabled",
                          lineHeight: 1.6,
                          mt: 1.5,
                        }}
                      >
                        批次制口径：锚定最近一轮测量批次（
                        {geoScore.batch_date}）；每维 n &lt; {geoScore.min_sample_n}{" "}
                        显示「样本不足」不出分；六维不合成单一总分（指标字典 §13.2）。
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* 历史诊断记录（区块 A 网站质量分的历史期，随区块 A 数据有无渲染） */}
          {current && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="h4">历史诊断记录</Typography>
                <Badge label={`${history.length} 期`} />
              </Box>
              <DataTable
                columns={columns}
                data={history}
                showPagination={false}
                getRowId={(row) => row.id}
                emptyText="暂无诊断记录"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
